import React, { useCallback, useMemo, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Grid, IconButton, Table, TableHead, TableBody, TableRow, TableCell } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import SydLabel from '../../../../../../../commonDesign/SydLabel'
import SydSelect from '../../../../../../../commonDesign/SydSelect'
import SydInput from '../../../../../../../commonDesign/SydInput'
import SydButton from '../../../../../../../commonDesign/Button'
import SydSwitch from '../../../../../../../commonDesign/SydSwitch'
import { formatDisplayName } from '../../../../../../../../utils/formatters'
import { blueprintExportFormats } from '../../../../../../../organisms/QueryBlueprint/admin/blueprintExportFormats'
import ColumnConfigEditor from './ColumnConfigEditor'

const AGGREGATE_FN_OPTIONS = [
  { label: 'Sum', value: 'sum' },
  { label: 'Average', value: 'avg' },
  { label: 'Min', value: 'min' },
  { label: 'Max', value: 'max' },
  { label: 'Count', value: 'count' },
  { label: 'First', value: 'first' },
  { label: 'Last', value: 'last' }
]

const AGGREGATION_MODE_OPTIONS = [
  { label: 'None (labels only)', value: 'none' },
  { label: 'Client-side aggregation', value: 'client' },
  { label: 'Server-provided depths', value: 'server' }
]

function HierarchicalTableConfigEditor ({ config, allPurposes, onUpdate }) {
  const [localConfig, setLocalConfig] = useState(config)

  useEffect(() => {
    setLocalConfig(config)
  }, [config])

  useEffect(() => {
    if (JSON.stringify(localConfig) !== JSON.stringify(config)) {
      onUpdate(localConfig)
    }
  }, [localConfig, config, onUpdate])

  const purposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'Select a purpose...', value: '__placeholder__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const footerPurposeOptions = useMemo(() => {
    const purposes = Object.entries(allPurposes || {})
    return [
      { label: 'None', value: '__none__' },
      ...purposes.map(([code, data]) => ({
        label: `${data.displayName} (${code})`,
        value: code
      }))
    ]
  }, [allPurposes])

  const selectedPurposeColumns = useMemo(() => {
    if (!localConfig.purposeCode || !allPurposes[localConfig.purposeCode]) return []
    return allPurposes[localConfig.purposeCode].columns || []
  }, [localConfig.purposeCode, allPurposes])

  const availableColumnsWithLabel = useMemo(() => {
    const labelCol = localConfig.hierarchy?.labelColumn || '_hierarchyLabel'
    const syntheticCol = { name: labelCol, dataType: 'string', description: 'Synthetic hierarchy label' }
    return [syntheticCol, ...selectedPurposeColumns]
  }, [selectedPurposeColumns, localConfig.hierarchy?.labelColumn])

  const columnSelectOptions = useMemo(() => {
    return selectedPurposeColumns.map(col => ({
      label: formatDisplayName(col.name),
      value: col.name
    }))
  }, [selectedPurposeColumns])

  const footerPurposeColumns = useMemo(() => {
    if (!localConfig.footerPurposeCode || !allPurposes[localConfig.footerPurposeCode]) return []
    return allPurposes[localConfig.footerPurposeCode].columns || []
  }, [localConfig.footerPurposeCode, allPurposes])

  // --- Purpose selection ---
  const handlePurposeChange = useCallback((purposeCode) => {
    const normalized = purposeCode === '__placeholder__' ? null : purposeCode
    const purpose = allPurposes[normalized]
    if (purpose && purpose.columns) {
      const prefillColumns = purpose.columns.map(col => ({
        name: col.name,
        displayName: formatDisplayName(col.name),
        description: col.description || null,
        formatString: null,
        align: ['integer', 'decimal'].includes(col.dataType) ? 'right' : 'left',
        sortable: col.sortable !== false,
        filterable: col.filterable !== false,
        hidden: col.hidden || false,
        exclude: false,
        width: 150
      }))
      setLocalConfig(prev => {
        const labelCol = prev.hierarchy?.labelColumn || '_hierarchyLabel'
        const labelEntry = {
          name: labelCol,
          displayName: 'Name',
          description: null,
          formatString: null,
          align: 'left',
          sortable: true,
          filterable: false,
          hidden: false,
          exclude: false,
          width: 300
        }
        return { ...prev, purposeCode: normalized, columns: [labelEntry, ...prefillColumns] }
      })
    } else {
      setLocalConfig(prev => ({ ...prev, purposeCode: normalized, columns: [] }))
    }
  }, [allPurposes])

  // --- Hierarchy levels ---
  const levels = localConfig.hierarchy?.levels || []

  const handleAddLevel = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: [...(prev.hierarchy?.levels || []), { groupBy: [], labelAccessor: '' }]
      }
    }))
  }, [])

  const handleRemoveLevel = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: (prev.hierarchy?.levels || []).filter((_, i) => i !== index)
      }
    }))
  }, [])

  const handleUpdateLevel = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        levels: (prev.hierarchy?.levels || []).map((lvl, i) =>
          i === index ? { ...lvl, [field]: value } : lvl
        )
      }
    }))
  }, [])

  const handleExpandDepthChange = useCallback((value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        defaultExpandDepth: Number(value) || 0
      }
    }))
  }, [])

  // --- Aggregation ---
  const aggregation = localConfig.hierarchy?.aggregation || { mode: 'none' }

  const handleAggregationModeChange = useCallback((mode) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          mode,
          columnAggregates: mode === 'client' ? (prev.hierarchy?.aggregation?.columnAggregates || []) : [],
          depthColumn: mode === 'server' ? (prev.hierarchy?.aggregation?.depthColumn || null) : null
        }
      }
    }))
  }, [])

  const handleAddAggregate = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          columnAggregates: [
            ...(prev.hierarchy?.aggregation?.columnAggregates || []),
            { column: '', fn: 'sum' }
          ]
        }
      }
    }))
  }, [])

  const handleRemoveAggregate = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          columnAggregates: (prev.hierarchy?.aggregation?.columnAggregates || []).filter((_, i) => i !== index)
        }
      }
    }))
  }, [])

  const handleUpdateAggregate = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          columnAggregates: (prev.hierarchy?.aggregation?.columnAggregates || []).map((agg, i) =>
            i === index ? { ...agg, [field]: value } : agg
          )
        }
      }
    }))
  }, [])

  const handleDepthColumnChange = useCallback((value) => {
    setLocalConfig(prev => ({
      ...prev,
      hierarchy: {
        ...prev.hierarchy,
        aggregation: {
          ...prev.hierarchy?.aggregation,
          depthColumn: value || null
        }
      }
    }))
  }, [])

  // --- Column management ---
  const handleAddColumn = useCallback((column) => {
    setLocalConfig(prev => ({ ...prev, columns: [...prev.columns, column] }))
  }, [])

  const handleRemoveColumn = useCallback((index) => {
    setLocalConfig(prev => ({ ...prev, columns: prev.columns.filter((_, i) => i !== index) }))
  }, [])

  const handleUpdateColumn = useCallback((index, updated) => {
    setLocalConfig(prev => ({
      ...prev,
      columns: prev.columns.map((col, i) => i === index ? updated : col)
    }))
  }, [])

  const handleColumnsChange = useCallback((newColumns) => {
    setLocalConfig(prev => ({ ...prev, columns: newColumns }))
  }, [])

  // --- Default sort ---
  const handleAddSortField = useCallback(() => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: [...(prev.defaultSort || []), { field: '', dir: 'asc' }]
    }))
  }, [])

  const handleRemoveSortField = useCallback((index) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.filter((_, i) => i !== index)
    }))
  }, [])

  const handleUpdateSortField = useCallback((index, field, value) => {
    setLocalConfig(prev => ({
      ...prev,
      defaultSort: prev.defaultSort.map((sort, i) =>
        i === index ? { ...sort, [field]: value } : sort
      )
    }))
  }, [])

  const sortFieldOptions = useMemo(() => {
    return (localConfig.columns || []).map(col => ({
      label: col.displayName || col.name,
      value: col.name
    }))
  }, [localConfig.columns])

  const sortDirOptions = [
    { label: 'Ascending', value: 'asc' },
    { label: 'Descending', value: 'desc' }
  ]

  // --- Footer ---
  const handleFooterPurposeChange = useCallback((footerPurposeCode) => {
    setLocalConfig(prev => ({
      ...prev,
      footerPurposeCode: footerPurposeCode === '__none__' ? null : footerPurposeCode,
      footerColumnMapping: []
    }))
  }, [])

  const getFooterColumnForMain = useCallback((mainColumnName) => {
    const mapping = (localConfig.footerColumnMapping || []).find(m => m.mainColumn === mainColumnName)
    return mapping?.footerColumn || '__none__'
  }, [localConfig.footerColumnMapping])

  const handleFooterMappingChange = useCallback((mainColumnName, footerColumnName) => {
    setLocalConfig(prev => {
      let newMappings = (prev.footerColumnMapping || []).filter(m => m.mainColumn !== mainColumnName)
      if (footerColumnName) {
        newMappings = [...newMappings, { mainColumn: mainColumnName, footerColumn: footerColumnName }]
      }
      return { ...prev, footerColumnMapping: newMappings }
    })
  }, [])

  const handleAutoConfigureFooterMapping = useCallback(() => {
    const footerColumnNames = new Set(footerPurposeColumns.map(c => c.name))
    const newMappings = (localConfig.columns || [])
      .filter(col => footerColumnNames.has(col.name))
      .map(col => ({ mainColumn: col.name, footerColumn: col.name }))
    setLocalConfig(prev => ({ ...prev, footerColumnMapping: newMappings }))
  }, [localConfig.columns, footerPurposeColumns])

  const footerColumnSelectOptions = useMemo(() => {
    return [
      { label: '(None)', value: '__none__' },
      ...footerPurposeColumns.map(col => ({ label: col.name, value: col.name }))
    ]
  }, [footerPurposeColumns])

  const mappedColumnCount = useMemo(() => {
    return (localConfig.footerColumnMapping || []).length
  }, [localConfig.footerColumnMapping])

  return (
    <Grid container spacing={2}>
      {/* Data Purpose Selection */}
      <Grid item xs={12}>
        <SydLabel required label='Data Configuration Purpose' helperText='Select which data purpose to display'>
          <SydSelect
            options={purposeOptions}
            value={localConfig.purposeCode || '__placeholder__'}
            onChange={(e) => handlePurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Hierarchy Levels */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Hierarchy Levels</h4>
          <SydButton
            size='sm'
            icon='add'
            onClick={handleAddLevel}
            disabled={!localConfig.purposeCode}
          >
            Add Level
          </SydButton>
        </div>
        <p style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)', marginTop: 0, marginBottom: '12px' }}>
          Define grouping levels from broadest to narrowest. Rows below the last level become leaf nodes.
        </p>
        {levels.length > 0
          ? (
            <Table className='opal-presentation-table zebra-rows' size='small' style={{ marginBottom: '12px' }}>
              <TableHead className='opt-thead'>
                <TableRow className='opt-tr'>
                  <TableCell className='opt-th' style={{ width: '40px' }}>#</TableCell>
                  <TableCell className='opt-th'>Group By Columns</TableCell>
                  <TableCell className='opt-th'>Label Column</TableCell>
                  <TableCell className='opt-th' style={{ width: '48px' }} />
                </TableRow>
              </TableHead>
              <TableBody className='opt-tbody'>
                {levels.map((level, index) => (
                  <TableRow className='opt-tr' key={index}>
                    <TableCell className='opt-td'>{index + 1}</TableCell>
                    <TableCell className='opt-td'>
                      <SydInput
                        value={(level.groupBy || []).join(', ')}
                        onChange={(e) => {
                          const cols = e.target.value.split(',').map(s => s.trim()).filter(Boolean)
                          handleUpdateLevel(index, 'groupBy', cols)
                        }}
                        placeholder='e.g. clientId, accountId'
                        size='sm'
                      />
                    </TableCell>
                    <TableCell className='opt-td'>
                      <SydSelect
                        options={[{ label: 'Select...', value: '__placeholder__' }, ...columnSelectOptions]}
                        value={level.labelAccessor || '__placeholder__'}
                        onChange={(e) => handleUpdateLevel(index, 'labelAccessor', e.target.value === '__placeholder__' ? '' : e.target.value)}
                        size='sm'
                      />
                    </TableCell>
                    <TableCell className='opt-td'>
                      <IconButton size='small' onClick={() => handleRemoveLevel(index)}>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )
          : (
            <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', margin: '0 0 12px' }}>
              No hierarchy levels configured. Add at least one level.
            </p>
          )}
        <SydLabel label='Default Expand Depth' helperText='How many levels to auto-expand on load (0 = all collapsed)'>
          <SydInput
            type='number'
            value={localConfig.hierarchy?.defaultExpandDepth ?? 1}
            onChange={(e) => handleExpandDepthChange(e.target.value)}
            style={{ maxWidth: '120px' }}
          />
        </SydLabel>
        <div style={{ marginTop: '12px' }}>
          <SydLabel label='Label Column Name' helperText='Synthetic field name written on every row with the display label. Reference this in your column config.'>
            <SydInput
              value={localConfig.hierarchy?.labelColumn ?? '_hierarchyLabel'}
              onChange={(e) => {
                const newLabel = e.target.value || '_hierarchyLabel'
                setLocalConfig(prev => {
                  const prevLabel = prev.hierarchy?.labelColumn || '_hierarchyLabel'
                  if (prevLabel === newLabel) {
                    return { ...prev, hierarchy: { ...prev.hierarchy, labelColumn: newLabel } }
                  }
                  return {
                    ...prev,
                    hierarchy: { ...prev.hierarchy, labelColumn: newLabel },
                    columns: (prev.columns || []).map(col =>
                      col.name === prevLabel ? { ...col, name: newLabel } : col
                    ),
                    defaultSort: (prev.defaultSort || []).map(s =>
                      s.field === prevLabel ? { ...s, field: newLabel } : s
                    )
                  }
                })
              }}
              placeholder='_hierarchyLabel'
              style={{ maxWidth: '240px' }}
            />
          </SydLabel>
        </div>
        <div style={{ marginTop: '12px' }}>
          <SydLabel label='Leaf Label Accessor' helperText='Source column for leaf row labels (e.g. assetName). Leave empty if leaves do not need a label.'>
            <SydSelect
              options={[{ label: '(None)', value: '__none__' }, ...columnSelectOptions]}
              value={localConfig.hierarchy?.leafLabelAccessor || '__none__'}
              onChange={(e) => setLocalConfig(prev => ({
                ...prev,
                hierarchy: { ...prev.hierarchy, leafLabelAccessor: e.target.value === '__none__' ? undefined : e.target.value }
              }))}
            />
          </SydLabel>
        </div>
      </Grid>

      {/* Aggregation */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Aggregation</h4>
        </div>
        <SydLabel label='Mode' helperText='How parent node values are computed'>
          <SydSelect
            options={AGGREGATION_MODE_OPTIONS}
            value={aggregation.mode || 'none'}
            onChange={(e) => handleAggregationModeChange(e.target.value)}
          />
        </SydLabel>

        {aggregation.mode === 'client'
          ? (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>Column Aggregates</span>
                <SydButton size='sm' icon='add' onClick={handleAddAggregate}>
                  Add
                </SydButton>
              </div>
              {(aggregation.columnAggregates || []).map((agg, index) => (
                <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
                  <div style={{ flex: 1 }}>
                    <SydSelect
                      options={[{ label: 'Select column...', value: '__placeholder__' }, ...columnSelectOptions]}
                      value={agg.column || '__placeholder__'}
                      onChange={(e) => handleUpdateAggregate(index, 'column', e.target.value === '__placeholder__' ? '' : e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <SydSelect
                      options={AGGREGATE_FN_OPTIONS}
                      value={agg.fn || 'sum'}
                      onChange={(e) => handleUpdateAggregate(index, 'fn', e.target.value)}
                    />
                  </div>
                  <IconButton size='small' onClick={() => handleRemoveAggregate(index)}>
                    <DeleteIcon fontSize='small' />
                  </IconButton>
                </div>
              ))}
            </div>
          )
          : null}

        {aggregation.mode === 'server'
          ? (
            <div style={{ marginTop: '12px' }}>
              <SydLabel label='Depth Column' helperText='Column in query results that indicates the row depth (0, 1, 2...)'>
                <SydSelect
                  options={[{ label: 'Select...', value: '' }, ...columnSelectOptions]}
                  value={aggregation.depthColumn || ''}
                  onChange={(e) => handleDepthColumnChange(e.target.value)}
                />
              </SydLabel>
            </div>
          )
          : null}
      </Grid>

      {/* Column Configuration */}
      <Grid item xs={12}>
        <ColumnConfigEditor
          columns={localConfig.columns || []}
          availableColumns={availableColumnsWithLabel}
          onAddColumn={handleAddColumn}
          onRemoveColumn={handleRemoveColumn}
          onUpdateColumn={handleUpdateColumn}
          onColumnsChange={handleColumnsChange}
        />
      </Grid>

      {/* Footer Purpose */}
      <Grid item xs={12}>
        <SydLabel label='Footer Data Purpose' helperText='Optional purpose for footer/summary data'>
          <SydSelect
            options={footerPurposeOptions}
            value={localConfig.footerPurposeCode || '__none__'}
            onChange={(e) => handleFooterPurposeChange(e.target.value)}
          />
        </SydLabel>
      </Grid>

      {/* Footer Column Mapping */}
      {localConfig.footerPurposeCode
        ? (
          <Grid item xs={12}>
            <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <h4 style={{ margin: 0 }}>Footer Column Mapping</h4>
              <span style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
                ({mappedColumnCount} of {(localConfig.columns || []).length} mapped)
              </span>
              <SydButton
                size='sm'
                icon='sync'
                onClick={handleAutoConfigureFooterMapping}
                disabled={(localConfig.columns || []).length === 0}
              >
                Auto-Configure
              </SydButton>
            </div>
            {(localConfig.columns || []).length > 0
              ? (
                <Table className='opal-presentation-table zebra-rows' size='small'>
                  <TableHead className='opt-thead'>
                    <TableRow className='opt-tr'>
                      <TableCell className='opt-th' style={{ width: '50%' }}>Main Column</TableCell>
                      <TableCell className='opt-th' style={{ width: '50%' }}>Footer Column</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody className='opt-tbody'>
                    {(localConfig.columns || []).map((col) => (
                      <TableRow className='opt-tr' key={col.name}>
                        <TableCell className='opt-td' style={{ fontWeight: 500 }}>
                          {col.displayName || col.name}
                        </TableCell>
                        <TableCell className='opt-td'>
                          <SydSelect
                            options={footerColumnSelectOptions}
                            value={getFooterColumnForMain(col.name)}
                            onChange={(e) => handleFooterMappingChange(col.name, e.target.value === '__none__' ? '' : e.target.value)}
                            size='sm'
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )
              : null}
          </Grid>
        )
        : null}

      {/* Default Sort */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <h4 style={{ margin: 0 }}>Default Sort Order</h4>
          <SydButton
            size='sm'
            icon='add'
            onClick={handleAddSortField}
            disabled={(localConfig.columns || []).length === 0}
          >
            Add Sort
          </SydButton>
        </div>
        {(localConfig.defaultSort || []).map((sort, index) => (
          <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
            <div style={{ flex: 1 }}>
              <SydSelect
                options={sortFieldOptions}
                value={sort.field}
                onChange={(e) => handleUpdateSortField(index, 'field', e.target.value)}
                placeholder='Select field'
              />
            </div>
            <div>
              <SydSelect
                options={sortDirOptions}
                value={sort.dir}
                onChange={(e) => handleUpdateSortField(index, 'dir', e.target.value)}
              />
            </div>
            <IconButton size='small' onClick={() => handleRemoveSortField(index)}>
              <DeleteIcon fontSize='small' />
            </IconButton>
          </div>
        ))}
        {(localConfig.defaultSort || []).length === 0
          ? (
            <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem', margin: 0 }}>
              No default sort configured
            </p>
          )
          : null}
      </Grid>

      {/* Export Settings */}
      <Grid item xs={12}>
        <div style={{ marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Export Settings</h4>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <SydSwitch
            value={!!localConfig.export?.enabled}
            onChange={(e, enabled) => setLocalConfig(prev => ({
              ...prev,
              export: enabled
                ? { ...prev.export, enabled: true, formats: prev.export?.formats || ['csv'] }
                : { ...prev.export, enabled: false }
            }))}
          />
          <span style={{ fontSize: '0.875rem' }}>Enable Export</span>
        </div>
        {localConfig.export?.enabled
          ? (
            <>
              <Table className='opal-presentation-table zebra-rows' size='small' style={{ marginBottom: '16px' }}>
                <TableBody className='opt-tbody'>
                  {blueprintExportFormats.map(fmt => (
                    <TableRow className='opt-tr' key={fmt.key}>
                      <TableCell className='opt-td' style={{ width: '48px' }}>
                        <SydSwitch
                          value={(localConfig.export?.formats || []).includes(fmt.key)}
                          onChange={(e, checked) => {
                            setLocalConfig(prev => {
                              const current = prev.export?.formats || []
                              const formats = checked
                                ? [...current, fmt.key]
                                : current.filter(f => f !== fmt.key)
                              if (!formats.length) {
                                return { ...prev, export: { ...prev.export, enabled: false, formats } }
                              }
                              return { ...prev, export: { ...prev.export, formats } }
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell className='opt-td'>{fmt.label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <SydLabel label='Default File Name' helperText='Pre-filled name when users export (optional)'>
                <SydInput
                  value={localConfig.export?.defaultFileName || ''}
                  onChange={(e) => setLocalConfig(prev => ({
                    ...prev,
                    export: { ...prev.export, defaultFileName: e.target.value || undefined }
                  }))}
                  placeholder='blueprint-export'
                />
              </SydLabel>
            </>
          )
          : null}
      </Grid>
    </Grid>
  )
}

HierarchicalTableConfigEditor.propTypes = {
  config: PropTypes.object.isRequired,
  allPurposes: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired
}

export default HierarchicalTableConfigEditor
